import React, { useState, FC, Fragment } from 'react';

import { isWidthUp, withWidth } from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';

import { useObserver } from 'mobx-react-lite';

import { isNullOrUndefined } from 'util';

import {
  BaseOrderingStore,
  OnlineOrder,
  ResultResponse,
  PromoCode
} from 'kiolyn-api';

import IOnlineOrderingRepository from '../../domain/repositories/IOnlineOrderingRepository';
import IOrderingRepository from '../../domain/repositories/IOrderingRepository';
import IPromoCodeRepository from '../../domain/repositories/IPromoCodeRepository';
import { ViewState } from '../../domain/models/ViewState';

import { OrderingProvider, useOrdering } from '../../stores/OrderingStore';
import { LocaleConfigProvider } from '../../stores/LocaleConfigProvider';

import OrderingPage from './OrderingPage';
import { LayoutVariant } from '../Core/CommonTypes';
import SubmitOnlineOrder from './SubmitOnlineOrder/SubmitOnlineOrder';
import IDeliveryRepository from '../../domain/repositories/IDeliveryRepository';
import IAuthRepository from '../../domain/repositories/IAuthRepository';
import {
  GoogleReCaptcha,
  GoogleReCaptchaProvider
} from 'react-google-recaptcha-v3';

class OrderingRepository implements IOrderingRepository {
  onlineOrderingRepository: IOnlineOrderingRepository;
  promoCodeRepository: IPromoCodeRepository;
  storeid: string;

  constructor(
    onlineOrderingRepository: IOnlineOrderingRepository,
    promoCodeRepository: IPromoCodeRepository,
    storeid: string
  ) {
    this.onlineOrderingRepository = onlineOrderingRepository;
    this.promoCodeRepository = promoCodeRepository;
    this.storeid = storeid;
  }

  public async loadStore(): Promise<BaseOrderingStore> {
    const store = await this.onlineOrderingRepository.loadStore(this.storeid);
    store.accept_order =
      store.accept_order === true &&
      (store.accept_delivery === true ||
        store.accept_pickup === true ||
        store.accept_dinein === true);
    store.show_category_types = true;
    if (store.delivery_settings && store.delivery_settings.delivery_fees) {
      store.delivery_settings.delivery_fees.sort(
        (s1, s2) => (s1.distance ?? 0) - (s2.distance ?? 0)
      );
    }
    return store;
  }

  public submitOrder(
    order: OnlineOrder,
    store: BaseOrderingStore,
    token: string
  ): Promise<ResultResponse> {
    if (isNullOrUndefined(store.id)) {
      return Promise.reject('Invalid store');
    }
    return this.onlineOrderingRepository.submitOrder(store.id, order, token);
  }

  public getPromoCodeByName(name: string): Promise<PromoCode | undefined> {
    return this.promoCodeRepository.getPromoCodeByName(this.storeid, name);
  }

  public getWebsocketForSubmitOrder(orderId: string): EventSource {
    return this.onlineOrderingRepository.getWebsocketForSubmitOrder(
      this.storeid,
      orderId
    );
  }

  public checkOrderStatus(orderUniqueId: string): Promise<boolean> {
    return this.onlineOrderingRepository.checkOrderStatus(
      this.storeid,
      orderUniqueId
    );
  }
}

interface WithWidthOnlineOrderingPageProps {
  storeid: string;
  tableId?: string;
  homeURL?: string;
  onlineOrderingRepository: IOnlineOrderingRepository;
  promoCodeRepository: IPromoCodeRepository;
  deliveryRepository: IDeliveryRepository;
  width: Breakpoint;
  authRepository: IAuthRepository;
}

export default withWidth()(
  ({
    width,
    storeid,
    tableId,
    homeURL,
    onlineOrderingRepository,
    promoCodeRepository,
    deliveryRepository,
    authRepository
  }: WithWidthOnlineOrderingPageProps) => {
    const variant = isWidthUp('md', width)
      ? LayoutVariant.laptop
      : LayoutVariant.mobile;
    const orderingRepository = new OrderingRepository(
      onlineOrderingRepository,
      promoCodeRepository,
      storeid
    );
    return (
      <OrderingProvider
        orderingRepository={orderingRepository}
        deliveryRepository={deliveryRepository}
        authRepository={authRepository}
      >
        <OnlineOrderingPage
          variant={variant}
          tableId={tableId}
          homeURL={homeURL}
        />
      </OrderingProvider>
    );
  }
);

interface OnlineOrderingPageProps {
  variant?: LayoutVariant;
  homeURL?: string;
  tableId?: string;
}

const OnlineOrderingPage: FC<OnlineOrderingPageProps> = ({
  variant,
  homeURL,
  tableId
}: OnlineOrderingPageProps) => {
  const orderingStore = useOrdering();
  const [showSubmitOrderDialog, setShowSubmitOrderDialog] = useState(false);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showInfoButton, setShowInfoButton] = useState(tableId ? false : true);
  const [showLoginButton, setShowLoginButton] = useState(false);
  const [showInfoOrderDialog, setShowInfoOrderDialog] = useState(false);

  const handleSubmitOrderClose = () => {
    setShowSubmitOrderDialog(false);
    if (
      variant === LayoutVariant.mobile &&
      orderingStore.submitState === ViewState.OK
    ) {
      setShowOrderDetail(false);
    }
    // Reset the submitting state
    orderingStore.submitState = ViewState.NONE;
    orderingStore.isNoDriverError = false;
  };

  const onRecaptchaVerify = (token: string) => {
    orderingStore.submitToken = token;
  };

  return useObserver(() => (
    <GoogleReCaptchaProvider reCaptchaKey="6LftFzwnAAAAADTFXuhn_HWwfIUHblS0F6ROnrr9">
      <LocaleConfigProvider locale={orderingStore.store.locale}>
        <Fragment>
          <GoogleReCaptcha
            onVerify={onRecaptchaVerify}
            refreshReCaptcha={orderingStore.shouldRefreshSubmitToken}
          />
          <OrderingPage
            variant={variant}
            onSubmit={() => {
              if (tableId) {
                orderingStore.order.order_type = 'dinein';
              }
              setShowSubmitOrderDialog(true);
            }}
            homeURL={homeURL}
            showOrderDetail={showOrderDetail}
            setShowOrderDetail={setShowOrderDetail}
            showInfoButton={showInfoButton}
            showLoginButton={showLoginButton}
            showInfoOrder={showInfoOrderDialog}
            setShowInfoButton={setShowInfoButton}
            setShowLoginButton={setShowLoginButton}
            setShowInfoOrderDialog={setShowInfoOrderDialog}
            defaultView={orderingStore.store.default_view}
          />
          {showSubmitOrderDialog && (
            <SubmitOnlineOrder
              onClose={handleSubmitOrderClose}
              variant={variant}
              tableId={tableId}
            />
          )}
        </Fragment>
      </LocaleConfigProvider>
    </GoogleReCaptchaProvider>
  ));
};
